import Logger from '../logger';
import gsap from 'gsap';

class ThemeHeroBanner {

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	/**
	 * Class constructor
	 */
	constructor(dom) {

		this.NAME = 'ThemeHeroBanner';

		this.dom = dom;
		this.imageMobile = this.dom.querySelector('.theme-hero-banner__background-image--mobile img');
		this.imageDesktop = this.dom.querySelector('.theme-hero-banner__background-image--desktop img');
		
		// Parallax settings - subtle and elegant
		this.parallaxSpeed = 0.1; // Subtle parallax effect (10% of scroll speed)
		this.animationMobile = null;
		this.animationDesktop = null;
	}

	/**
	 * init - Initialize the component
	 **/
	init() {
		Logger.info(`${this.NAME} > init`);

		// Only initialize if images exist
		if (!this.imageMobile && !this.imageDesktop) {
			Logger.warn(`${this.NAME} > No images found, skipping parallax initialization`);
			return;
		}

		// Set up initial state
		this.setupImages();
		
		// Use GSAP's smooth scroll-based animation
		this.setupParallax();
	}

	/**
	 * setupImages - Prepare images for parallax effect
	 */
	setupImages() {
		// Make images slightly taller than container to allow for subtle parallax movement
		// Keep it minimal to prevent white space
		if (this.imageMobile) {
			this.imageMobile.style.height = '110%';
			this.imageMobile.style.objectPosition = 'center center';
		}

		if (this.imageDesktop) {
			this.imageDesktop.style.height = '110%';
			this.imageDesktop.style.objectPosition = 'center center';
		}
	}

	/**
	 * setupParallax - Set up GSAP-based smooth parallax animation
	 */
	setupParallax() {
		// Throttled scroll handler for smooth performance
		this.handleScroll = this.throttle(() => {
			this.updateParallax();
		}, 16); // ~60fps

		// Handle resize to recalculate
		this.handleResize = this.debounce(() => {
			this.updateParallax();
		}, 250);

		window.addEventListener('scroll', this.handleScroll, { passive: true });
		window.addEventListener('resize', this.handleResize, { passive: true });
		
		// Initial calculation
		this.updateParallax();
	}

	/**
	 * updateParallax - Update parallax position using GSAP for smooth animation
	 */
	updateParallax() {
		const rect = this.dom.getBoundingClientRect();
		const windowHeight = window.innerHeight;
		const scrollY = window.scrollY || window.pageYOffset;
		const elementHeight = rect.height;
		
		// Calculate element's position relative to viewport
		const elementTop = rect.top + scrollY;
		
		// Calculate how much of the element is visible
		// This creates a more restrained parallax that's proportional to visibility
		const elementCenter = elementTop + (elementHeight / 2);
		const viewportCenter = scrollY + (windowHeight / 2);
		const distanceFromCenter = viewportCenter - elementCenter;
		
		// Limit parallax to a small range to prevent white space
		// Only move when element is near viewport center for elegant effect
		const maxOffset = elementHeight * 0.05; // Max 5% of element height
		const parallaxOffset = Math.max(-maxOffset, Math.min(maxOffset, distanceFromCenter * this.parallaxSpeed));
		
		// Only apply parallax when element is visible
		if (rect.bottom >= 0 && rect.top <= windowHeight) {
			// Use GSAP for smooth, hardware-accelerated animation
			if (this.imageMobile && window.innerWidth < 720) { // Mobile breakpoint
				gsap.to(this.imageMobile, {
					y: -parallaxOffset,
					duration: 0.3,
					ease: 'power1.out',
					overwrite: true
				});
			}
			
			if (this.imageDesktop && window.innerWidth >= 720) { // Tablet/Desktop breakpoint
				gsap.to(this.imageDesktop, {
					y: -parallaxOffset,
					duration: 0.3,
					ease: 'power1.out',
					overwrite: true
				});
			}
		} else {
			// Reset transform when out of view using GSAP
			if (this.imageMobile) {
				gsap.to(this.imageMobile, {
					y: 0,
					duration: 0.3,
					ease: 'power1.out',
					overwrite: true
				});
			}
			if (this.imageDesktop) {
				gsap.to(this.imageDesktop, {
					y: 0,
					duration: 0.3,
					ease: 'power1.out',
					overwrite: true
				});
			}
		}
	}

	/**
	 * throttle - Throttle function calls
	 */
	throttle(func, limit) {
		let inThrottle;
		return function () {
			const args = arguments;
			const context = this;
			if (!inThrottle) {
				func.apply(context, args);
				inThrottle = true;
				setTimeout(() => inThrottle = false, limit);
			}
		};
	}

	/**
	 * debounce - Debounce function calls
	 */
	debounce(func, wait) {
		let timeout;
		return function () {
			const context = this;
			const args = arguments;
			clearTimeout(timeout);
			timeout = setTimeout(() => func.apply(context, args), wait);
		};
	}

	/**
	 * destroy - Clean up event listeners and GSAP animations
	 */
	destroy() {
		if (this.handleScroll) {
			window.removeEventListener('scroll', this.handleScroll);
		}
		if (this.handleResize) {
			window.removeEventListener('resize', this.handleResize);
		}
		// Kill any active GSAP animations
		if (this.animationMobile) {
			this.animationMobile.kill();
		}
		if (this.animationDesktop) {
			this.animationDesktop.kill();
		}
		// Reset transforms
		if (this.imageMobile) {
			gsap.set(this.imageMobile, { clearProps: 'transform' });
		}
		if (this.imageDesktop) {
			gsap.set(this.imageDesktop, { clearProps: 'transform' });
		}
	}
}

export default ThemeHeroBanner;
